import React from 'react';

/**
 * Global React ErrorBoundary.
 *
 * Any uncaught render error inside a child component would otherwise blow up
 * the entire React tree, leaving the user with a completely blank white
 * screen. By wrapping the app (or specific routes) in this boundary, we
 * convert that into a graceful in-place error UI with a retry/back action.
 *
 * Crashes are also surfaced to the backend logger so we can spot them quickly.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Something went wrong' };
  }

  componentDidCatch(error, errorInfo) {
    // Best-effort error reporting. Failure is silent.
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/chat-event`;
      const body = JSON.stringify({
        event: 'frontend_crash',
        question: (error?.message || '').slice(0, 200),
        destination: (typeof window !== 'undefined' ? window.location.pathname : '').slice(0, 64),
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
      }
    } catch (_) {}
    // eslint-disable-next-line no-console
    console.error('Render error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, message: '' });
  };

  handleHome = () => {
    if (typeof window !== 'undefined') window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-red-100 p-6 text-center" data-testid="error-boundary-card">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m0 3.75h.008v.008H12v-.008zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-slate-900 mb-1 font-['Outfit']">Something went wrong</h2>
            <p className="text-sm text-slate-500 mb-4">
              We hit an unexpected error while loading this page. Try again or head back to your dashboard.
            </p>
            {this.state.message && (
              <div className="bg-red-50 border border-red-100 text-red-700 text-xs rounded-lg p-2 mb-4 font-mono break-all">
                {this.state.message}
              </div>
            )}
            <div className="flex gap-2 justify-center">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                data-testid="error-boundary-retry"
              >
                Try again
              </button>
              <button
                onClick={this.handleHome}
                className="px-4 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="error-boundary-home"
              >
                Go to dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
